import { useEffect, useState, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import Container from '@components/Container';
import Button from '@components/Button';
import { useAuth } from '@lib/useAuth';
import { getUserSessions, updateSessionNotes } from '@lib/sessions';
import { listBreathingRatios } from '@lib/breathingRatio';
import { listBreathingStyles } from '@lib/breathingStyles';
import { listBackgroundVideos } from '@lib/backgroundVideos';
import type { BreathingSession } from '@lib/sessions';
import type {
  BreathingRatio,
  BreathingStyleRecord,
  BackgroundVideo,
} from '@lib/api';

const SESSION_PAGE_SIZE = 10;

function formatMinutesSeconds(totalSeconds: number) {
  const m = Math.floor((totalSeconds || 0) / 60);
  const s = Math.floor((totalSeconds || 0) % 60);
  return `${m}m ${s}s`;
}

interface SessionDateParts {
  date: string;
  time: string;
}

function formatSessionDateForDisplay(
  session: BreathingSession
): SessionDateParts {
  const raw =
    session.created_at || session.session_date || new Date().toISOString();

  const dt = new Date(raw);
  if (Number.isNaN(dt.getTime())) return { date: '', time: '' };

  const timeZone = session.user_timezone || undefined;

  const dateOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone,
  } as const;
  const timeOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone,
  } as const;

  try {
    const dateStr = new Intl.DateTimeFormat(undefined, dateOptions).format(dt);
    const timeStr = new Intl.DateTimeFormat(undefined, timeOptions).format(dt);
    return {
      date: `${dateStr},`,
      time: timeStr,
    };
  } catch {
    const fallbackDate = dt.toLocaleDateString(undefined, dateOptions);
    const fallbackTime = dt.toLocaleTimeString(undefined, timeOptions);
    return {
      date: `${fallbackDate},`,
      time: fallbackTime,
    };
  }
}

export default function AllSessions() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sessions, setSessions] = useState<BreathingSession[]>([]);
  const [ratios, setRatios] = useState<BreathingRatio[]>([]);
  const [breathingStyles, setBreathingStyles] = useState<
    BreathingStyleRecord[]
  >([]);
  const [backgroundVideos, setBackgroundVideos] = useState<BackgroundVideo[]>(
    []
  );
  const [selectedRatioId, setSelectedRatioId] = useState<string | 'all'>('all');
  const [selectedVisibility, setSelectedVisibility] = useState<
    'all' | 'public' | 'private'
  >('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<
    'breathing' | 'duration' | 'cycles' | 'created'
  >('created');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editingNotes, setEditingNotes] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesError, setNotesError] = useState<string | null>(null);

  const handleSortHeaderClick = (
    column: 'breathing' | 'duration' | 'cycles' | 'created'
  ) => {
    if (sortBy === column) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDir('asc');
    }
  };

  const handleHeaderClick =
    (column: 'breathing' | 'duration' | 'cycles' | 'created') =>
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      handleSortHeaderClick(column);
    };

  const startEditingNotes = (session: BreathingSession) => {
    setEditingSessionId(session.id);
    setEditingNotes(session.notes || '');
    setNotesError(null);
  };

  const cancelEditingNotes = () => {
    setEditingSessionId(null);
    setEditingNotes('');
    setNotesError(null);
  };

  const saveEditingNotes = async () => {
    if (!editingSessionId) return;
    setNotesSaving(true);
    setNotesError(null);
    try {
      const normalizedNotes =
        editingNotes.trim().length === 0 ? null : editingNotes;
      const updated = await updateSessionNotes(
        editingSessionId,
        normalizedNotes
      );
      setSessions((prev) =>
        prev.map((session) => (session.id === updated.id ? updated : session))
      );
      cancelEditingNotes();
    } catch (error) {
      console.error(error);
      setNotesError('Unable to save notes. Please try again.');
    } finally {
      setNotesSaving(false);
    }
  };
  const [visibleCount, setVisibleCount] = useState<number>(SESSION_PAGE_SIZE);

  useEffect(() => {
    // Reset visible count when filter or sort options change
    setVisibleCount(SESSION_PAGE_SIZE);
  }, [selectedRatioId, selectedVisibility, sortBy, sortDir, sessions.length]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!user) return;
      setLoading(true);
      try {
        const [s, r, styles, bvs] = await Promise.all([
          getUserSessions(user.id),
          listBreathingRatios(),
          listBreathingStyles(),
          listBackgroundVideos(),
        ]);
        if (cancelled) return;
        setSessions(s || []);
        setRatios(r || []);
        setBreathingStyles(styles || []);
        setBackgroundVideos(bvs || []);
      } catch (e) {
        if (!cancelled) setError('Unable to load sessions.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (!user) {
    return (
      <Container className="pt-12">
        <div className="card mb-12 text-center md:mb-16">
          <h2 className="gradient-text mb-4 pb-6 text-4xl font-bold">
            My Sessions
          </h2>
          <p className="mb-4">Please sign in to view your sessions.</p>
          <div className="flex justify-center">
            <Button variant="primary" onClick={() => navigate('/signin')}>
              Sign In
            </Button>
          </div>
        </div>
      </Container>
    );
  }

  const getBreathingName = (session: BreathingSession) => {
    const ratio = session.breathing_ratio_id
      ? ratios.find((r) => r.id === session.breathing_ratio_id)
      : null;
    return ratio?.name || 'Custom';
  };

  const getBreathingStyleName = (session: BreathingSession) => {
    if (!session.breathing_style_id) return 'None';
    const style = breathingStyles.find(
      (item) => item.id === session.breathing_style_id
    );
    const name = style?.name?.trim();
    return name || 'Unknown';
  };

  const getBackgroundName = (session: BreathingSession) => {
    if (!session.background_videos_id) return 'None';
    const bg = backgroundVideos.find(
      (b) => b.id === session.background_videos_id
    );
    return bg?.name || 'Unknown';
  };

  const filteredSessions = (sessions || []).filter((session) => {
    if (selectedRatioId === 'all') return true;
    if (selectedRatioId === 'custom') return !session.breathing_ratio_id;
    return String(session.breathing_ratio_id) === selectedRatioId;
  });

  // Apply privacy filter
  const privacyFiltered = filteredSessions.filter((session) => {
    if (selectedVisibility === 'all') return true;
    if (selectedVisibility === 'public')
      return Boolean(session.leaderboard_eligibility);
    return !session.leaderboard_eligibility;
  });

  const sortedSessions = privacyFiltered.slice();
  const multiplier = sortDir === 'asc' ? 1 : -1;
  sortedSessions.sort((a, b) => {
    if (sortBy === 'breathing') {
      const na = getBreathingName(a).toLowerCase();
      const nb = getBreathingName(b).toLowerCase();
      if (na < nb) return -1 * multiplier;
      if (na > nb) return 1 * multiplier;
      return 0;
    }

    if (sortBy === 'duration') {
      const da = a.total_duration || 0;
      const db = b.total_duration || 0;
      return (da - db) * multiplier;
    }

    if (sortBy === 'cycles') {
      const ca = a.cycles_completed || 0;
      const cb = b.cycles_completed || 0;
      return (ca - cb) * multiplier;
    }

    const ta = new Date(a.created_at || a.session_date).getTime() || 0;
    const tb = new Date(b.created_at || b.session_date).getTime() || 0;
    return (ta - tb) * multiplier;
  });

  const visibleSessions = sortedSessions.slice(0, visibleCount);
  const hasMoreSessions = visibleSessions.length < sortedSessions.length;

  return (
    <Container className="pb-16 pt-16">
      <div className="mx-auto max-w-5xl pb-16 text-center">
        <h1 className="gradient-text mb-4 pb-2 text-4xl font-bold sm:text-5xl">
          My Sessions
        </h1>
        <p className="text-lg text-gray-600">
          Review your full breathing history, filter by breathing style, and
          sort by duration, cycles, or date to spot patterns, celebrate streaks,
          and refine your practice over time.
        </p>
      </div>

      <section className="rounded-2xl border border-gray-100 bg-white p-6 shadow-lg">
        {loading ? (
          <div className="text-center text-sm text-gray-500">
            Loading sessions…
          </div>
        ) : error ? (
          <div className="text-center text-sm text-rose-600">{error}</div>
        ) : (
          <>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex flex-col gap-1"></div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-500">
                  Showing {visibleSessions.length} of {privacyFiltered.length}{' '}
                  sessions
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="item-selected whitespace-nowrap"
                  onClick={() => navigate('/stats')}
                >
                  View Breathing Stats
                </Button>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3 rounded-3xl px-4 py-3">
              <div className="flex w-auto min-w-[150px] flex-col gap-1">
                <span className="text-[11px] font-semibold uppercase tracking-[0.15em]">
                  Filter By
                </span>
                <select
                  value={selectedRatioId}
                  onChange={(e) => setSelectedRatioId(e.target.value as string)}
                  className="rounded-2xl border border-transparent bg-white px-3 py-2 text-sm font-medium text-slate-900 shadow-inner transition hover:border-slate-300 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-200"
                >
                  <option value="all">All Breathing</option>
                  <option value="custom">Custom</option>
                  {ratios.map((r) => (
                    <option key={r.id} value={String(r.id)}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex w-auto min-w-[150px] flex-col gap-1">
                <span className="text-[11px] font-semibold uppercase tracking-[0.15em]">
                  Privacy
                </span>
                <select
                  value={selectedVisibility}
                  onChange={(e) =>
                    setSelectedVisibility(
                      e.target.value as 'all' | 'public' | 'private'
                    )
                  }
                  className="rounded-2xl border border-transparent bg-white px-3 py-2 text-sm font-medium text-slate-900 shadow-inner transition hover:border-slate-300 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-200"
                >
                  <option value="all">All</option>
                  <option value="public">Public</option>
                  <option value="private">Private</option>
                </select>
              </div>
            </div>

            {/* Sorting is handled via table headers (desktop) */}

            {/* Mobile card list + desktop/table layout for sessions */}
            <div className="mt-4 space-y-4">
              {visibleSessions.length === 0 ? (
                <div className="rounded-xl border border-gray-100 bg-white p-8 text-center">
                  <div className="text-gray-500">No sessions found.</div>
                  <div className="mt-4">
                    <Button
                      type="button"
                      variant="primary"
                      size="lg"
                      onClick={() => navigate('/dashboard')}
                      className="begin-breathing-button"
                    >
                      Begin Breathing
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  {/* Mobile-friendly card list (small screens) */}
                  <div className="space-y-3 md:hidden">
                    {visibleSessions.map((session) => {
                      const label = formatSessionDateForDisplay(session);
                      return (
                        <div
                          key={session.id}
                          className="rounded-lg border border-gray-100 bg-white p-3 shadow-sm"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-baseline justify-between gap-2">
                                <div className="truncate text-sm font-semibold text-gray-900">
                                  {getBreathingName(session)}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {label.date} {label.time}
                                </div>
                              </div>

                              {editingSessionId === session.id ? (
                                <div className="mt-2 flex flex-col gap-2">
                                  <textarea
                                    rows={2}
                                    value={editingNotes}
                                    onChange={(e) =>
                                      setEditingNotes(e.target.value)
                                    }
                                    className="min-h-[48px] w-full rounded-xl border border-slate-200 bg-white/70 px-3 py-2 text-sm text-slate-900 shadow-inner outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
                                  />
                                  <div className="flex flex-wrap items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={saveEditingNotes}
                                      disabled={notesSaving}
                                      className="item-selected inline-flex items-center gap-1 rounded-full bg-primary-600 px-3 py-1.5 text-sm font-semibold tracking-wider text-white transition disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                      Save
                                    </button>
                                    <button
                                      type="button"
                                      onClick={cancelEditingNotes}
                                      disabled={notesSaving}
                                      className="item-selected inline-flex items-center gap-1 rounded-full border border-gray-300 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-slate-400"
                                    >
                                      Cancel
                                    </button>
                                    {notesError && (
                                      <span className="text-xs text-red-500">
                                        {notesError}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ) : session.notes ? (
                                <div className="mt-2 flex items-center gap-2 truncate text-xs text-gray-600">
                                  <span className="truncate">
                                    {session.notes}
                                  </span>
                                  <button
                                    type="button"
                                    title="Edit notes"
                                    onClick={() => startEditingNotes(session)}
                                    className="-mr-1 ml-2 inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-400 transition hover:bg-slate-100 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-200"
                                  >
                                    <span
                                      aria-hidden="true"
                                      className="text-sm leading-none"
                                    >
                                      ✏️
                                    </span>
                                    <span className="sr-only">Edit notes</span>
                                  </button>
                                </div>
                              ) : null}
                            </div>

                            <div className="flex flex-col items-end justify-between text-right">
                              <div className="text-sm font-semibold text-primary-600">
                                {formatMinutesSeconds(
                                  session.total_duration || 0
                                )}
                              </div>
                              <div className="mt-1 text-xs text-gray-700">
                                Cycles: {session.cycles_completed}
                              </div>
                              <div className="mt-2">
                                <span
                                  className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${session.leaderboard_eligibility ? 'border-green-200 bg-green-50 text-green-800' : 'border-red-200 bg-red-50 text-red-800'}`}
                                >
                                  {session.leaderboard_eligibility
                                    ? 'Public'
                                    : 'Private'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Desktop/table view (md+) */}
                  <div className="mt-2 hidden overflow-x-auto rounded-xl border border-gray-100 md:block">
                    <table className="min-w-full text-left text-sm text-gray-800">
                      <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        <tr>
                          <th className="w-[16%] px-4 py-3 font-bold uppercase">
                            <button
                              type="button"
                              onClick={handleHeaderClick('created')}
                              className="flex items-center gap-2"
                            >
                              <span className="font-bold uppercase">
                                Date and Time
                              </span>
                              {sortBy === 'created' && (
                                <span className="text-xs text-gray-400">
                                  {sortDir === 'asc' ? '⬆️' : '⬇️'}
                                </span>
                              )}
                            </button>
                          </th>

                          <th className="w-[18%] px-4 py-3 font-bold uppercase">
                            <button
                              type="button"
                              onClick={handleHeaderClick('breathing')}
                              className="flex items-center gap-2"
                            >
                              <span className="font-bold uppercase">
                                Breathing
                              </span>
                              {sortBy === 'breathing' && (
                                <span className="text-xs text-gray-400">
                                  {sortDir === 'asc' ? '⬆️' : '⬇️'}
                                </span>
                              )}
                            </button>
                          </th>

                          <th className="w-[10%] px-4 py-3 font-bold uppercase">
                            <button
                              type="button"
                              onClick={handleHeaderClick('duration')}
                              className="flex items-center gap-2"
                            >
                              <span className="font-bold uppercase">
                                Duration
                              </span>
                              {sortBy === 'duration' && (
                                <span className="text-xs text-gray-400">
                                  {sortDir === 'asc' ? '⬆️' : '⬇️'}
                                </span>
                              )}
                            </button>
                          </th>

                          <th className="w-[10%] px-4 py-3 font-bold uppercase">
                            <button
                              type="button"
                              onClick={handleHeaderClick('cycles')}
                              className="flex items-center gap-2"
                            >
                              <span className="font-bold uppercase">
                                Cycles
                              </span>
                              {sortBy === 'cycles' && (
                                <span className="text-xs text-gray-400">
                                  {sortDir === 'asc' ? '⬆️' : '⬇️'}
                                </span>
                              )}
                            </button>
                          </th>

                          <th className="px-4 py-3">
                            <span className="font-bold uppercase">
                              Background
                            </span>
                          </th>
                          <th className="px-4 py-3">
                            <span className="font-bold uppercase">Notes</span>
                          </th>
                          <th className="px-4 py-3 text-center">
                            <span className="font-bold uppercase">Privacy</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleSessions.map((session) => {
                          const label = formatSessionDateForDisplay(session);
                          return (
                            <tr
                              key={session.id}
                              className="border-t border-gray-100 hover:bg-indigo-50"
                            >
                              <td className="px-4 py-3 align-top">
                                <div className="font-semibold text-gray-900">
                                  {label.date}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {label.time}
                                </div>
                              </td>

                              <td className="px-4 py-3 align-top">
                                <div className="text-sm font-semibold text-gray-900">
                                  {getBreathingName(session)}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {getBreathingStyleName(session)}
                                </div>
                              </td>

                              <td className="px-4 py-3 align-top text-sm font-semibold text-primary-600">
                                {formatMinutesSeconds(
                                  session.total_duration || 0
                                )}
                              </td>

                              <td className="px-4 py-3 align-top text-sm text-gray-700">
                                {session.cycles_completed}
                              </td>

                              <td className="px-4 py-3 align-top text-sm text-gray-700">
                                {getBackgroundName(session)}
                              </td>

                              <td className="px-4 py-3 align-top">
                                {editingSessionId === session.id ? (
                                  <div className="flex flex-col gap-2">
                                    <textarea
                                      rows={2}
                                      value={editingNotes}
                                      onChange={(e) =>
                                        setEditingNotes(e.target.value)
                                      }
                                      className="min-h-[48px] w-full rounded-xl border border-slate-200 bg-white/70 px-3 py-2 text-sm text-slate-900 shadow-inner outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
                                    />
                                    <div className="flex flex-wrap items-center gap-2">
                                      <button
                                        type="button"
                                        onClick={saveEditingNotes}
                                        disabled={notesSaving}
                                        className="item-selected inline-flex items-center gap-1 rounded-full bg-primary-600 px-3 py-1.5 text-sm font-semibold tracking-wider text-white transition disabled:cursor-not-allowed disabled:opacity-50"
                                      >
                                        Save
                                      </button>
                                      <button
                                        type="button"
                                        onClick={cancelEditingNotes}
                                        disabled={notesSaving}
                                        className="item-selected inline-flex items-center gap-1 rounded-full border border-gray-300 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-slate-400"
                                      >
                                        Cancel
                                      </button>
                                      {notesError && (
                                        <span className="text-xs text-red-500">
                                          {notesError}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1">
                                    <span className="truncate">
                                      {session.notes || ''}
                                    </span>
                                    <button
                                      type="button"
                                      title="Edit notes"
                                      onClick={() => startEditingNotes(session)}
                                      className="-mr-1 ml-2 inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-400 transition hover:bg-slate-100 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-200"
                                    >
                                      <span
                                        aria-hidden="true"
                                        className="text-sm leading-none"
                                      >
                                        ✏️
                                      </span>
                                      <span className="sr-only">
                                        Edit notes
                                      </span>
                                    </button>
                                  </div>
                                )}
                              </td>

                              <td className="px-4 py-3 text-center align-top">
                                <span
                                  className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${session.leaderboard_eligibility ? 'border-green-200 bg-green-50 text-green-800' : 'border-red-200 bg-red-50 text-red-800'}`}
                                >
                                  {session.leaderboard_eligibility
                                    ? 'Public'
                                    : 'Private'}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
            {hasMoreSessions && (
              <div className="mt-3 flex flex-col gap-3 px-1 sm:flex-row sm:items-center sm:justify-between">
                <span className="text-xs text-gray-600">
                  Showing {visibleSessions.length} of {sortedSessions.length}{' '}
                  sessions
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="item-selected w-full whitespace-nowrap sm:w-auto"
                  onClick={() =>
                    setVisibleCount((prev) =>
                      Math.min(prev + SESSION_PAGE_SIZE, sortedSessions.length)
                    )
                  }
                >
                  Show next {SESSION_PAGE_SIZE}
                </Button>
              </div>
            )}
          </>
        )}
      </section>
    </Container>
  );
}
